import { Segmented, Switch } from 'antd';
import { Data, Layout, Shape } from 'plotly.js';
import { useCallback, useState } from 'react';
import PlotlyComponent from 'react-plotly.js';
import { Dfs2SpectralResponse, IWaveSpectraTimeseries } from '../../../../../../types/waveSpectra';
import {
  SPECTRA_CHART_LABELS as axisTitles,
  closeDirectionSeam,
  DIRECTION_DOMAIN,
  DIRECTION_TICK_STEP,
  findNearestTime,
  formatCursorTime,
  FREQUENCY_DOMAIN,
  getInitialTime,
  reorderByIndex,
  SPECTRUM_COLOR_SCALE,
  SPECTRUM_COLOR_SCALE_WHITE_MIN,
  SpectrumAxisMode,
  TIME_CURSOR_LINE,
  TIMESERIES_DOMAIN,
  TIMESERIES_LINE_COLOR,
  transpose,
  UI_REVISION_TOKEN,
  wrapDirectionAxis,
} from './helpers';
import { spectraChartConfig } from './spectra-charts.config';
import styles from './WaveSpectra.module.scss';
import { WaveSpectraEnergyDensityPolar } from './WaveSpectraEnergyDensityPolar';

const Plot = PlotlyComponent as any;

interface WaveSpectraHeatmapsProps {
  data: Dfs2SpectralResponse;
  timeseries: IWaveSpectraTimeseries | null;
}

const hoverTemplateEnergyDensityPlot =
  't: %{x|%Y-%m-%d %H:%M}<br>θ: %{y:.1f}°<br>S(θ): %{z:.3e} m²/deg<extra></extra>';

export const WaveSpectraHeatmaps = ({ data, timeseries }: WaveSpectraHeatmapsProps) => {
  const { metadata, derived } = data;

  const [axisMode, setAxisMode] = useState<SpectrumAxisMode>('period');
  const [isLightPlotTheme, setIsLightPlotTheme] = useState(true);
  const colorScale = isLightPlotTheme ? SPECTRUM_COLOR_SCALE_WHITE_MIN : SPECTRUM_COLOR_SCALE;

  const [timeCursor, setTimeCursor] = useState({
    time: getInitialTime(metadata.times),
    rev: 0,
  });

  const cursorTime = metadata.times.includes(timeCursor.time)
    ? timeCursor.time
    : getInitialTime(metadata.times);

  const moveTimeCursor = (x: string | number) =>
    setTimeCursor((prev) => ({
      time: findNearestTime(metadata.times, x),
      rev: prev.rev + 1,
    }));

  const handleClick = (event: { points?: Array<{ x?: string | number }> }) => {
    const x = event?.points?.[0]?.x;
    if (x != null) {
      moveTimeCursor(x);
    }
  };

  const handleRelayout = (event: Record<string, string | number>) => {
    const x = event?.['shapes[0].x0'] ?? event?.['shapes[0].x1'];
    if (x != null) {
      moveTimeCursor(x);
    }
  };

  const spectra = derived[0];

  // S_theta from GetSpectraAnalysis (backend) is already normalized to m²/deg
  // regardless of the source item's angular unit - no further conversion here.
  const sThetaPerDeg = spectra.S_theta;

  const { dirs: wrappedDirs, order: dirOrder } = wrapDirectionAxis(metadata.dirs);
  const orderedRows = reorderByIndex(transpose(sThetaPerDeg), dirOrder);
  const { dirs: closedDirs, rows: closedRows } = closeDirectionSeam(wrappedDirs, orderedRows);

  const getHoverTemplateTimeSeriesPlot = useCallback((shortName: string, unit: string) => {
    return `t: %{x|%Y-%m-%d %H:%M}<br>${shortName}: %{y:.2f} ${unit}<extra></extra>`;
  }, []);

  const timeseriesTraces: Partial<Data>[] = timeseries
    ? [
        {
          type: 'scatter',
          mode: 'lines',
          x: timeseries.times,
          y: timeseries.values,
          yaxis: 'y3',
          line: { color: TIMESERIES_LINE_COLOR },
          showlegend: false,
          hovertemplate: getHoverTemplateTimeSeriesPlot(timeseries.shortName, timeseries.unit),
        },
      ]
    : [];

  const hoverTemplatePeriodOrFreqPlot =
    axisMode === 'period'
      ? 't: %{x|%Y-%m-%d %H:%M}<br>T: %{y:.1f} s<br>S(f): %{z:.3e} m²/Hz<extra></extra>'
      : 't: %{x|%Y-%m-%d %H:%M}<br>f: %{y:.4f} Hz<br>S(f): %{z:.3e} m²/Hz<extra></extra>';

  const traces: Partial<Data>[] = [
    ...timeseriesTraces,
    {
      type: 'heatmap',
      x: metadata.times,
      y: axisMode === 'period' ? metadata.periods : metadata.freqs,
      z: transpose(spectra.S_f),
      zmin: 0,
      colorscale: colorScale,
      colorbar: {
        len: FREQUENCY_DOMAIN[1] - FREQUENCY_DOMAIN[0],
        y: FREQUENCY_DOMAIN[1],
        yanchor: 'top',
        thickness: 14,
        title: {
          text: axisTitles.directional,
          side: 'right',
        },
        tickfont: { size: 10 },
        exponentformat: 'e',
      },
      hovertemplate: hoverTemplatePeriodOrFreqPlot,
    },
    {
      type: 'heatmap',
      x: metadata.times,
      y: closedDirs,
      z: closedRows,
      yaxis: 'y2',
      zmin: 0,
      colorscale: colorScale,
      colorbar: {
        len: DIRECTION_DOMAIN[1] - DIRECTION_DOMAIN[0],
        y: DIRECTION_DOMAIN[0],
        yanchor: 'bottom',
        thickness: 14,
        title: { text: axisTitles.energyDensity, side: 'right' },
        tickfont: { size: 10 },
        exponentformat: 'e',
      },
      hovertemplate: hoverTemplateEnergyDensityPlot,
    },
  ];

  const timeCursorShape = {
    type: 'line',
    xref: 'x',
    yref: 'paper',
    x0: cursorTime,
    x1: cursorTime,
    y0: 0,
    y1: 1,
    line: TIME_CURSOR_LINE,
  } as Partial<Shape>;

  const layout: Partial<Layout> = {
    autosize: true,
    margin: { l: 70, r: 110, t: 40, b: 55 },
    xaxis: {
      type: 'date',
      anchor: 'y2',
      title: { text: 'Time' },
      tickfont: { size: 10 },
      uirevision: UI_REVISION_TOKEN,
    },
    yaxis: {
      type: axisMode === 'period' ? 'linear' : 'log',
      domain: FREQUENCY_DOMAIN,
      title: { text: axisMode === 'period' ? axisTitles.periodAxis : axisTitles.frequencyXAxis },
      tickfont: { size: 10 },
      uirevision: `${UI_REVISION_TOKEN}-${axisMode}`,
    },
    yaxis2: {
      domain: DIRECTION_DOMAIN,
      title: { text: axisTitles.frequencyYAxis },
      tick0: 0,
      dtick: DIRECTION_TICK_STEP,
      range: [0, 360],
      tickfont: { size: 10 },
      uirevision: UI_REVISION_TOKEN,
    },
    yaxis3: {
      domain: TIMESERIES_DOMAIN,
      title: {
        text: timeseries
          ? `${timeseries.shortName} (${timeseries.unit})`
          : axisTitles.timeSeriesAxis,
      },
      tickfont: { size: 10 },
      uirevision: UI_REVISION_TOKEN,
    },
    shapes: [timeCursorShape],
    annotations: [
      {
        text: '',
        // text: axisTitles.timeSeriesChartTitle,
        x: 0.5,
        y: TIMESERIES_DOMAIN[1],
        xref: 'paper',
        yref: 'paper',
        yanchor: 'bottom',
        yshift: 8,
        showarrow: false,
        font: { size: 13 },
      },
      {
        text: '',
        // text: axisTitles.frequencyChartTitle,
        x: 0.5,
        y: FREQUENCY_DOMAIN[1],
        xref: 'paper',
        yref: 'paper',
        yanchor: 'bottom',
        yshift: 8,
        showarrow: false,
        font: { size: 13 },
      },
      {
        text: '',
        // text: axisTitles.directionalChartTitle,
        x: 0.5,
        y: DIRECTION_DOMAIN[1],
        xref: 'paper',
        yref: 'paper',
        yanchor: 'bottom',
        yshift: 8,
        showarrow: false,
        font: { size: 13 },
      },
    ],
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
  };

  const item = data.items?.[0];

  return (
    <>
      <div className={styles.waveSpectra__timestamp}>
        {`${formatCursorTime(cursorTime)}`}
        <Segmented
          size="small"
          value={axisMode}
          onChange={(value) => setAxisMode(value as SpectrumAxisMode)}
          options={[
            { label: 'Period (s)', value: 'period' },
            { label: 'Frequency (Hz)', value: 'frequency' },
          ]}
          style={{ marginLeft: 12 }}
        />
        <Switch
          size="small"
          checked={isLightPlotTheme}
          onChange={setIsLightPlotTheme}
          style={{ marginLeft: 12 }}
        />
        <span style={{ marginLeft: 8 }}>Light theme</span>
      </div>
      <div className={styles.waveSpectra__chart}>
        <Plot
          data={traces}
          layout={layout}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
          config={{ ...spectraChartConfig, edits: { shapePosition: true } }}
          onClick={handleClick}
          onRelayout={handleRelayout}
        />
      </div>
      {item?.spectrum_2d && (
        <div className={styles.waveSpectra__polarCharts}>
          <div className={styles.waveSpectra__polarChart}>
            <WaveSpectraEnergyDensityPolar
              metadata={metadata}
              item={item}
              time={cursorTime}
              axisMode={axisMode}
              colorScale={colorScale}
              isLightPlotTheme={isLightPlotTheme}
            />
          </div>
          {/* <div className={styles.waveSpectra__polarChart}>
            <WaveSpectraEnergyDensityContour
              metadata={metadata}
              item={item}
              time={cursorTime}
              axisMode={axisMode}
            />
          </div> */}
        </div>
      )}
    </>
  );
};
